import { MoreVertOutlined } from '@mui/icons-material';
import { IconButton, Menu } from '@mui/material';
import { use, useState } from 'react';

import { graphql, type DocumentType } from '@/gql';

import { CurrentUserContext } from '@/context/CurrentUserContext';

import { PostDeleteMenuItem } from './options-button/PostDeleteMenuItem';
import { PostCopyLinkMenuItem } from './options-button/PostCopyLinkMenuItem';
import { PostEditTitleMenuItem } from './options-button/PostEditTitleMenuItem';
import { PostEditTopicsMenuItem } from './options-button/PostEditTopicsMenuItem';

const POST_OPTIONS_BUTTON_POST_FRAGMENT = graphql(`
  fragment postOptionsButtonPostFragment on Post {
    ...postCopyLinkMenuItemPostFragment
    ...postDeleteMenuItemPostFragment
    ...postEditTitleMenuItemPostFragment
    ...postEditTopicsMenuItemPostFragment
    _id
    author {
      _id
    }
  }
`);

const POST_OPTIONS_BUTTON_GROUP_FRAGMENT = graphql(`
  fragment postOptionsButtonGroupFragment on Group {
    ...postCopyLinkMenuItemGroupFragment
    ...postDeleteMenuItemGroupFragment
    ...postEditTopicsMenuItemGroupFragment
    _id
    userCanEditAllPosts
    userCanDeleteAllPosts
  }
`);
interface Props {
  post: DocumentType<typeof POST_OPTIONS_BUTTON_POST_FRAGMENT>;
  group: DocumentType<typeof POST_OPTIONS_BUTTON_GROUP_FRAGMENT>;
  serviceMachineName?: string | null;
  location?: 'myHub' | 'adminHub' | null;
}

export const PostOptionsButton = ({
  post,
  group,
  serviceMachineName,
  location,
}: Props) => {
  const { _id: postId, author } = post;
  const { _id: authorId } = author ?? {};
  const { userCanDeleteAllPosts, userCanEditAllPosts } = group;

  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const { currentUserId } = use(CurrentUserContext);

  const canDeletePost = authorId === currentUserId || userCanDeleteAllPosts;
  const canEditPost = authorId === currentUserId || userCanEditAllPosts;

  const isAdmin = userCanDeleteAllPosts && userCanEditAllPosts;

  const handleClose = () => {
    setMenuAnchor(null);
  };

  return (
    <>
      <IconButton
        aria-haspopup="true"
        size="large"
        onClick={(event) => setMenuAnchor(event.currentTarget)}
      >
        <MoreVertOutlined />
      </IconButton>
      <Menu
        keepMounted
        disableEnforceFocus
        disableRestoreFocus
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleClose}
      >
        {postId && (
          <PostCopyLinkMenuItem
            post={post}
            group={group}
            onClose={handleClose}
          />
        )}
        {canDeletePost && (
          <PostDeleteMenuItem
            post={post}
            group={group}
            onClose={handleClose}
            serviceMachineName={serviceMachineName}
            location={location}
          />
        )}
        {canEditPost && (
          <PostEditTitleMenuItem post={post} onClose={handleClose} />
        )}
        {isAdmin && (
          <PostEditTopicsMenuItem
            post={post}
            group={group}
            onClose={handleClose}
          />
        )}
      </Menu>
    </>
  );
};
